

































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';

import { pmLessonWebservice } from '@/webservices/pmlesson';
import type { PMPhase } from '@/models/pmphase';
import type { PMSubPhase } from '@/models/pmsubphase';
import type { PMLesson } from '@/models/pmlesson';
import type { User } from '@/models/user';
import StoreUpdatePMLessonModal from '@/components/knowledge/pm/lessons/StoreUpdatePMLessonModal.vue';
import SearchField from '@/components/prine/SearchField.vue';
import type { Company } from '@/models/company';
import { hasAnyPermission, hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import { groupedPMLessonPermissions } from '@/helpers/auth/commonPermissions';
import type { Tag } from '@/models/tag';
import type { Lesson } from '@/models/lesson';
import TagSelect from '@/components/form/TagSelect.vue';
import type { XProcess } from '@/models/xprocess';

@Component({
    name: 'PMPhasesList',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasAnyPermission, hasPermission },
    components: {
        TagSelect,
        StoreUpdatePMLessonModal,
        SearchField,
    },
})
export default class PMPhasesList extends Vue {
    private filteredPhases = new Array<PMPhase>();
    private subphases = new Array<PMSubPhase>();
    private isStoreUpdateLessonModalActive: boolean = false;
    private lessonsCount: number = 0;
    private personalLessonsCount: number = 0;

    private tableSettings = {
        openedDetailed: Array<number>(),
        isStriped: true,
        isHoverable: true,
        isPaginated: false,
    };

    private selectedPhase: PMPhase = {} as PMPhase;
    private selectedSubPhase: PMSubPhase = {} as PMSubPhase;
    private selectedLesson: PMLesson = {} as PMLesson;
    private isSearching: boolean = false;

    get selectedTag(): Tag | null {
        return this.$store.getters['knowledge/pmSelectedTag'];
    }

    set selectedTag(value: Tag | null) {
        this.$store.commit('knowledge/setPmSelectedTag', value);
    }

    @Getter('knowledge/pmSearchString')
    private searchQuery: string;
    private filterByLessons: boolean = hasPermission(PERMISSIONS.APPEX_PM_LESSON);

    @Prop() private originalPhases: PMPhase[];

    @Getter('account/userInfo') private userInfo: User;
    @Getter('account/company') private company: Company;
    @Getter('utils/currentLocale') private currentLocale: string;

    @Watch('originalPhases')
    private onOriginalPhasesChange() {
        this.prepareData();
    }

    @Watch('searchQuery')
    private onSearchQueryValueChange() {
        this.search();
    }

    @Watch('selectedTag')
    private onSelectedTagChange() {
        this.search();
    }

    private mounted() {
        this.prepareData();
        this.getLessonsCount();
        this.getPersonalLessonsCount();
    }

    private updateSearchQuery(value: string) {
        this.$store.commit('knowledge/setPmSearchString', value);
    }

    private search() {
        this.isSearching = true;

        const regExQuery = new RegExp(this.searchQuery.trim(), 'i');

        let originalPhasesCopy: PMPhase[];
        originalPhasesCopy = JSON.parse(JSON.stringify(this.originalPhases));

        this.filteredPhases = originalPhasesCopy
            .filter((phase: PMPhase) => {
                let totalLessonsCount: number = 0;
                const phaseTitleMatch = phase.title.search(regExQuery) !== -1;
                if (this.filterByLessons) {
                    const filteredSubPhases = phase.sub_phases.filter((subPhase: PMSubPhase) => {
                        const filteredLessons = subPhase.lessons.filter((lesson: PMLesson) => {
                            if (lesson.more_info) {
                                return (
                                    lesson.insight.search(regExQuery) !== -1 ||
                                    lesson.more_info.search(regExQuery) !== -1
                                );
                            } else {
                                return lesson.insight.search(regExQuery) !== -1;
                            }
                        });
                        subPhase.lessons = filteredLessons;
                        subPhase.lessons_count = filteredLessons.length;
                        totalLessonsCount += filteredLessons.length;

                        const subPhaseTitleMatch = subPhase.title.search(regExQuery) !== -1;

                        return subPhaseTitleMatch || filteredLessons.length > 0;
                    });
                    phase.sub_phases = filteredSubPhases;
                    phase.totalLessonsCount = totalLessonsCount;

                    return phaseTitleMatch || filteredSubPhases.length > 0;
                } else {
                    return phaseTitleMatch;
                }
            })
            .filter((phase: PMPhase) => {
                if (this.selectedTag && this.selectedTag.id !== 0) {
                    let totalLessonsCount = 0;
                    let hasTaggedLessons = false;
                    phase.sub_phases.forEach((subPhase: PMSubPhase) => {
                        subPhase.lessons = this.filterLessonsByTag(subPhase.lessons);
                        subPhase.lessons_count = subPhase.lessons.length;
                        totalLessonsCount += subPhase.lessons.length;
                        if (subPhase.lessons.length > 0) {
                            hasTaggedLessons = true;
                        }
                    });
                    phase.totalLessonsCount = totalLessonsCount;
                    return hasTaggedLessons;
                }
                return true;
            });

        this.isSearching = false;
    }

    private filterLessonsByTag(lessons: PMLesson[]): PMLesson[] {
        const selectedTag = this.selectedTag;
        if (!selectedTag || selectedTag.id === 0) {
            return lessons;
        }

        return lessons.filter((lesson: PMLesson) => {
            if (lesson.sub_lessons && lesson.sub_lessons.length > 0) {
                lesson.sub_lessons = this.filterLessonsByTag(lesson.sub_lessons);
            }

            const hasTag = lesson.tags ? lesson.tags.some((tag) => tag.id === selectedTag.id) : false;
            return hasTag || (lesson.sub_lessons && lesson.sub_lessons.length > 0);
        });
    }

    private prepareData() {
        this.filteredPhases = JSON.parse(JSON.stringify(this.originalPhases));

        if (this.$route.query.searchQuery && this.$route.query.searchQuery !== '') {
            this.$store.commit(
                'knowledge/setPmSearchString',
                decodeURIComponent(this.$route.query.searchQuery.toString())
            );
            if (this.$route.query.filterByLessons) {
                this.filterByLessons = this.$route.query.filterByLessons === 'true';
            }
        }
        this.search();
    }

    private getLessonsCount() {
        pmLessonWebservice
            .count()
            .then((response) => {
                this.lessonsCount = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private getPersonalLessonsCount() {
        pmLessonWebservice
            .countPersonal()
            .then((response) => {
                this.personalLessonsCount = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private toggleStoreUpdatePMLessonModal(pmPhase: PMPhase, pmSubPhase: PMSubPhase, pmLesson: PMLesson) {
        if (pmSubPhase) {
            this.selectedPhase = pmPhase;
            this.selectedSubPhase = pmSubPhase;
        }
        this.selectedLesson = pmLesson;
        this.isStoreUpdateLessonModalActive = !this.isStoreUpdateLessonModalActive;
    }

    private rowClicked(row) {
        if (hasAnyPermission(groupedPMLessonPermissions)) {
            this.$router.push({
                name: 'GroupedPMLessonsList',
                query: {
                    phaseId: row.id,
                    searchQuery: encodeURIComponent(this.searchQuery),
                    filterByLessons: this.filterByLessons.toString(),
                    selectedTagId: this.selectedTag && this.selectedTag.id ? this.selectedTag.id.toString() : 'all',
                },
            });
        }
    }

    private clearSearch() {
        this.$store.commit('knowledge/clearSearchStrings');
    }
}
